/**
 * Affiliates — non-employee affiliate list. PageHeader + two tabs:
 * "Active" (confirmed non-employee affiliates, referrer_type: 'non_employee')
 * and "Candidates" (contacts eligible to become affiliates). Active tab is
 * persisted in URL `?tab=` like CustomersPage.
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Percent } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { AffiliateActiveListSection } from './components/affiliate-active-list-section';
import { AffiliateCandidatesListSection } from './components/affiliate-candidates-list-section';

const TABS = [
  { value: 'active', label: 'Active' },
  { value: 'candidates', label: 'Candidates' },
] as const;

type TabValue = (typeof TABS)[number]['value'];

export default function AffiliatesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlTab = searchParams.get('tab') as TabValue | null;
  const activeTab: TabValue = TABS.some((t) => t.value === urlTab)
    ? (urlTab as TabValue)
    : 'active';

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      <PageHeader
        title="Affiliates"
        description="Manage referral partners, track commissions and review candidates."
        actions={
          <>
            <Button
              variant="secondary"
              startIcon={<Percent size={15} />}
              onClick={() => navigate('/affiliates/commission-policies')}
            >
              Commission policies
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={() => navigate('/affiliates/new')}>
              Add affiliate
            </Button>
          </>
        }
      />

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="active">
          {activeTab === 'active' && (
            <AffiliateActiveListSection referrerType="non_employee" />
          )}
        </TabsContent>

        <TabsContent value="candidates">
          {activeTab === 'candidates' && <AffiliateCandidatesListSection />}
        </TabsContent>
      </Tabs>
    </Container>
  );
}
